
/**
 * Knowledge base for Bluetoothcash.net AI assistant
 */

export type Language = 'en' | 'es' | 'fr';

export interface KnowledgeBase {
  responses: Record<string, string[]>;
  contextualResponses: Record<string, string[]>;
  fallbacks: string[];
}

// English knowledge base
export const enKnowledge: KnowledgeBase = {
  responses: {
    // Bluetooth knowledge
    bluetooth: [
      "Bluetoothcash uses secure Bluetooth technology to transfer funds directly between devices.",
      "Only verified client devices can receive Bluetooth payments. This ensures maximum security.",
      "Our Bluetooth payments work with any Bluetooth-enabled device with the Bluetoothcash client app."
    ],
    
    // Stripe knowledge
    stripe: [
      "Bluetoothcash integrates with Stripe for secure credit card processing and verification.",
      "All card details are tokenized through Stripe before processing for enhanced security.",
      "We use Stripe's robust fraud detection systems and AI enhancement to protect all transactions.",
      "Our system supports test card numbers like 4242 4242 4242 4242, 4000 0566 5566 5556, and 5555 5555 5555 4444 for testing."
    ],
    
    // Payment methods
    payment: [
      "Bluetoothcash offers secure payments via Bluetooth to verified clients. Non-verified recipients will be redirected to email transfer.",
      "All transactions are protected with end-to-end encryption for maximum security.",
      "To make a payment, scan for nearby verified devices and select your recipient or use Stripe for card payments."
    ],
    
    // Transactions knowledge
    transactions: [
      "All transactions on Bluetoothcash are securely processed and recorded in our system.",
      "Transaction history is accessible from your account dashboard and shows all payment details.",
      "For card transactions, we use Stripe's secure payment processing with AI verification for added security."
    ],
    
    // Security knowledge
    security: [
      "Bluetoothcash prioritizes security with end-to-end encryption for all transfers.",
      "We verify all receiving clients to ensure payments only go to authorized devices.",
      "Your payment data is never stored permanently on any device, and card details are securely tokenized."
    ],
    
    // Card verification
    "card verification": [
      "Bluetoothcash verifies all cards through Stripe's secure system with additional AI checks.",
      "Our AI verification system scores cards on a scale of 0-100 for risk assessment.",
      "For testing, you can use Stripe test cards like 4242 4242 4242 4242, which will always pass verification."
    ],
    
    // Bluetooth API knowledge
    "bluetooth api": [
      "The Web Bluetooth API allows websites to communicate with Bluetooth devices.",
      "Bluetoothcash uses the navigator.bluetooth.requestDevice() method to discover nearby devices.",
      "Our app requires the 'bluetooth' permission to scan for and connect to devices."
    ],
    
    // Stripe API knowledge
    "stripe api": [
      "Bluetoothcash integrates with Stripe's secure payment processing API.",
      "Our implementation uses Stripe's client and server SDKs for secure payment handling.",
      "Stripe's API provides tokenization, payment processing, and fraud prevention.",
      "We use Stripe test cards for development: 4242 4242 4242 4242 (success), 4000 0566 5566 5556 (success), and 5555 5555 5555 4444 (Mastercard success)."
    ],
    
    // Stripe AI knowledge
    "stripe ai": [
      "We use AI-powered fraud detection to verify all card transactions.",
      "Our system analyzes transaction patterns to identify potentially fraudulent activity.",
      "Custom AI enhancements provide additional security layers for safe transactions.",
      "The AI verification system produces a risk score between 0-100, with higher scores indicating lower risk."
    ],
    
    // Test cards knowledge
    "test cards": [
      "You can use these Stripe test cards: 4242 4242 4242 4242 (Visa), 4000 0566 5566 5556 (Visa debit), or 5555 5555 5555 4444 (Mastercard).",
      "For all test cards, use any future expiry date (MM/YY), any 3-digit CVC, and any postal code.",
      "These cards will always pass verification in test mode and can be used for development."
    ]
  },
  
  contextualResponses: {
    "/payment": [
      "On the payment page, you can choose between Bluetooth, email, or Stripe payment methods.",
      "For Bluetooth payments, both devices need to have Bluetooth enabled and be in proximity.",
      "All payment methods are secured with our advanced encryption technology."
    ],
    
    "/bluetooth": [
      "This page shows Bluetooth devices that can receive payments. Only verified clients can receive direct transfers.",
      "You can scan for nearby devices by clicking the 'Scan for Devices' button.",
      "If your recipient isn't a verified client, you'll be redirected to email transfer automatically."
    ],
    
    "/payment/stripe": [
      "This page allows you to make secure payments using Stripe's payment processing.",
      "Your card details are securely processed through Stripe and never stored on our servers.",
      "Our AI-enhanced Stripe integration provides additional fraud protection for your transactions."
    ],
    
    "/transactions": [
      "This page displays your transaction history, showing all payments made and received.",
      "Each transaction record includes details like amount, recipient, date, and payment method used.",
      "You can filter transactions by date, amount, or payment method to find specific records."
    ]
  },
  
  fallbacks: [
    "I'm your Bluetoothcash assistant. How can I help with your secure payment needs today?",
    "Bluetoothcash offers secure transfers using Bluetooth technology and Stripe payments. What would you like to know?",
    "I can help you understand how to make payments, manage your account, or troubleshoot any issues with Bluetoothcash.",
    "Bluetoothcash prioritizes security in all transactions. Feel free to ask about our security features or payment options."
  ]
};

// Spanish knowledge base (shortened for brevity)
export const esKnowledge: KnowledgeBase = {
  responses: {
    bluetooth: [
      "Bluetoothcash utiliza tecnología Bluetooth segura para transferir fondos directamente entre dispositivos.",
      "Solo los dispositivos cliente verificados pueden recibir pagos Bluetooth. Esto garantiza la máxima seguridad."
    ],
    
    stripe: [
      "Bluetoothcash se integra con Stripe para procesamiento y verificación segura de tarjetas de crédito.",
      "Todos los datos de tarjetas se tokenizarán a través de Stripe antes del procesamiento para mayor seguridad."
    ],
    
    payment: [
      "Bluetoothcash ofrece pagos seguros a través de Bluetooth a clientes verificados.",
      "Todas las transacciones están protegidas con cifrado de extremo a extremo para máxima seguridad."
    ],
    
    transactions: [
      "Todas las transacciones en Bluetoothcash se procesan y registran de forma segura en nuestro sistema.",
      "El historial de transacciones es accesible desde el panel de control de su cuenta."
    ],
    
    "test cards": [
      "Puede utilizar estas tarjetas de prueba de Stripe: 4242 4242 4242 4242 (Visa), 4000 0566 5566 5556 (débito Visa) o 5555 5555 5555 4444 (Mastercard)."
    ]
  },
  
  contextualResponses: {
    "/payment": [
      "En la página de pago, puede elegir entre métodos de pago Bluetooth, correo electrónico o Stripe."
    ],
    
    "/transactions": [
      "Esta página muestra su historial de transacciones, con todos los pagos realizados y recibidos."
    ]
  },
  
  fallbacks: [
    "Soy su asistente de Bluetoothcash. ¿Cómo puedo ayudarle con sus necesidades de pago seguro hoy?",
    "Bluetoothcash ofrece transferencias seguras mediante tecnología Bluetooth y pagos Stripe. ¿Qué le gustaría saber?"
  ]
};

// French knowledge base (shortened for brevity)
export const frKnowledge: KnowledgeBase = {
  responses: {
    bluetooth: [
      "Bluetoothcash utilise la technologie Bluetooth sécurisée pour transférer des fonds directement entre appareils.",
      "Seuls les appareils clients vérifiés peuvent recevoir des paiements Bluetooth. Cela garantit une sécurité maximale."
    ],
    
    stripe: [
      "Bluetoothcash s'intègre à Stripe pour un traitement et une vérification sécurisés des cartes de crédit.",
      "Toutes les données de carte sont tokenisées via Stripe avant le traitement pour une sécurité renforcée."
    ],
    
    transactions: [
      "Toutes les transactions sur Bluetoothcash sont traitées et enregistrées en toute sécurité dans notre système.",
      "L'historique des transactions est accessible depuis le tableau de bord de votre compte."
    ],
    
    "test cards": [
      "Vous pouvez utiliser ces cartes de test Stripe: 4242 4242 4242 4242 (Visa), 4000 0566 5566 5556 (débit Visa) ou 5555 5555 5555 4444 (Mastercard)."
    ]
  },
  
  contextualResponses: {
    "/payment": [
      "Sur la page de paiement, vous pouvez choisir entre les méthodes de paiement Bluetooth, e-mail ou Stripe."
    ],
    
    "/transactions": [
      "Cette page affiche votre historique de transactions, montrant tous les paiements effectués et reçus."
    ]
  },
  
  fallbacks: [
    "Je suis votre assistant Bluetoothcash. Comment puis-je vous aider avec vos besoins de paiement sécurisé aujourd'hui?",
    "Bluetoothcash offre des transferts sécurisés utilisant la technologie Bluetooth et les paiements Stripe. Que souhaitez-vous savoir?"
  ]
};

export const knowledgeBase: Record<Language, KnowledgeBase> = {
  en: enKnowledge,
  es: esKnowledge,
  fr: frKnowledge
};
